/**
 * P3-S1 릴레이 순서 배치·진행 API(C-07 · 세션 역할 teacher · 코스 경계 — 계약 §3):
 *   GET  /api/courses/{c}/teacher/room/relay → { enabled, def, plan, state, current, paused, stages, students }
 *        · enabled = Phase 3 플래그(field.phase3.relay) — 꺼져 있으면 콘솔 탭을 숨긴다
 *   PUT  …/teacher/room/relay/plan { stages:[{pid, in, out, timeoutS?}] } → 200
 *        · 422 { field:"plan", issues:[{stage, code, message}] }(정합 · 구간 수 · 같은 기계 · 종류 · 타임아웃)
 *        · 422 { error:"locked" } 또는 409 — 이미 시작했다(Setup 에서만 바꾼다)
 *        · 모드가 relay 가 아니면 룸 서버가 relay 로 바꾼다(월드 다시 싣기)
 *   POST …/teacher/room/relay { op: start|pause|resume|retry|skip, stage? } → 200
 *        · 422 { error: locked · invalid-plan · not-running · not-current · state } · 503 FE-303
 * 구간 번호는 0 부터(룸 서버 RelayMachine 과 같다 — 화면만 +1). 이름(별명)은 제어 평면이 붙인다(교사에게만).
 */
import { useMemo } from "react";
import { ApiError } from "../../../lib/http/errors";
import { useSession, useSessionManager } from "../../../store/session/SessionProvider";
import { readIssues, readRelay } from "../lib/relay";
import { payload } from "../lib/relay";
import type { Requester } from "../../../types/api";
import type { RelayOp, RelayLatest, RelayDraft, RelayInfo, RelayIssue } from "../types";

/** 422 { field:"plan", issues } — 서버 정합 검사가 받지 않았다 */
export class PlanRejected extends Error {
  constructor(readonly issues: RelayIssue[]) {
    super("plan rejected");
    this.name = "PlanRejected";
  }
}

/** 422 { error } · 409 — 릴레이 명령·배치를 지금 상태에서 받지 않는다 */
export class RelayRejected extends Error {
  constructor(readonly reason: string) {
    super(`relay rejected: ${reason}`);
    this.name = "RelayRejected";
  }
}

export interface RelayApi {
  get(): Promise<RelayInfo>;
  savePlan(d: RelayDraft): Promise<void>;
  cmd(op: RelayOp, stage?: number): Promise<void>;
}

const obj = (v: unknown): Record<string, unknown> | null =>
  v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : null;

const rejected = (e: unknown): never => {
  if (e instanceof ApiError && e.status === 409) throw new RelayRejected("locked");
  if (e instanceof ApiError && e.status === 422) {
    const b = obj(e.body);
    if (Array.isArray(b?.issues)) throw new PlanRejected(readIssues(b.issues));
    const why = b?.error ?? b?.field;
    throw new RelayRejected(typeof why === "string" ? why : "state");
  }
  throw e;
};

export function createRelayApi(request: Requester, courseId: string): RelayApi {
  const c = `/api/courses/${encodeURIComponent(courseId)}/teacher/room/relay`;
  const send = (method: string, body: unknown): RequestInit => ({
    method,
    body: JSON.stringify(body),
    headers: { "Content-Type": "application/json" },
  });
  return {
    get: async () => readRelay(await request<unknown>(c)),
    async savePlan(d) {
      try {
        await request<unknown>(`${c}/plan`, send("PUT", payload(d)));
      } catch (e) {
        rejected(e);
      }
    },
    async cmd(op, stage) {
      try {
        await request<unknown>(c, send("POST", stage === undefined ? { op } : { op, stage }));
      } catch (e) {
        rejected(e);
      }
    },
  };
}

export function useRelayApi(): RelayApi | null {
  const manager = useSessionManager();
  const state = useSession();
  const courseId =
    state.status === "ready" && state.session.role === "teacher" ? state.session.courseId : null;
  return useMemo(
    () => (courseId ? createRelayApi(manager.request, courseId) : null),
    [manager, courseId],
  );
}

// ── P3-S2 학생 · 교사 공통 — 완성 뒤 '다시 보기'(FS-14-01) 진입 ─────────────────────

const LATEST_STATES: readonly RelayLatest["state"][] = [
  "none",
  "setup",
  "running",
  "done",
  "aborted",
];

export function readRelayLatest(v: unknown): RelayLatest {
  const o = obj(v) ?? {};
  const st = typeof o.state === "string" ? o.state : "";
  return {
    enabled: o.enabled === true,
    state: (LATEST_STATES as readonly string[]).includes(st)
      ? (st as RelayLatest["state"])
      : "none",
    replayId: typeof o.replayId === "string" && o.replayId ? o.replayId : null,
    finishedAt: typeof o.finishedAt === "string" ? o.finishedAt : null,
  };
}

export function createRelayLatestApi(request: Requester, courseId: string) {
  const path = `/api/courses/${encodeURIComponent(courseId)}/relay/latest`;
  return { latest: async () => readRelayLatest(await request<unknown>(path)) };
}
export type RelayLatestApi = ReturnType<typeof createRelayLatestApi>;

/** 세션 코스(학생 · 교사)의 relay/latest — 세션이 없으면 null */
export function useRelayLatestApi(): RelayLatestApi | null {
  const manager = useSessionManager();
  const state = useSession();
  const courseId = state.status === "ready" ? state.session.courseId : null;
  return useMemo(
    () => (courseId ? createRelayLatestApi(manager.request, courseId) : null),
    [manager, courseId],
  );
}
